import { CommonModule } from '@angular/common';
import {
  AfterViewChecked,
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { LogService } from '@wheere-front/shared-util';
import { WhButtonComponent } from './wh-button.component';

interface LevelOption {
  /** Label for the chip. */
  name: string;
  /** Internal id — number for compactness. */
  value: number;
  /** Substring matched against the line — same convention inside admin uses
   * (`INF`/`WRN`/`DBG`/`ERR`). */
  match: string;
}

const LEVELS: LevelOption[] = [
  { name: 'Info', value: 1, match: 'INF' },
  { name: 'Warn', value: 2, match: 'WRN' },
  { name: 'Debug', value: 3, match: 'DBG' },
  { name: 'Error', value: 4, match: 'ERR' },
];

/**
 * Per-theme accent palette. Drives the active-state colour of the level filter
 * chips, the focus rings on download/clear buttons, and the scrollbar tint. The
 * terminal background stays dark in every theme — operators rely on the high
 * contrast for readability.
 */
const THEME = {
  tracker: {
    accent: '#ED107D', // primary-500 (Wheere pink)
    accentSoft: '#FCE7F3',
    accentText: '#831843',
  },
  gateway: {
    accent: '#F59E0B', // warmYellow / amber-500
    accentSoft: '#FEF3C7',
    accentText: '#78350F',
  },
} as const;

type Theme = keyof typeof THEME;

@Component({
  selector: 'lib-logs-viewer',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, FormsModule, WhButtonComponent],
  styles: [
    `
      :host {
        display: block;
        height: 100%;
      }
      .terminal {
        flex: 1;
        background: #0f0f0f;
        color: #e5e7eb;
        font-family: Consolas, 'Courier New', Courier, monospace;
        font-size: 12px;
        line-height: 1.55;
        padding: 12px 14px;
        overflow: auto;
        white-space: pre-wrap;
        word-break: break-all;
      }
      .terminal-line {
        white-space: pre-wrap;
      }
      :host ::ng-deep .terminal-line .timestamp {
        color: #9ca3af;
      }
      :host ::ng-deep .terminal-line .brackets {
        color: #6b7280;
      }
    `,
  ],
  template: `
    <div
      class="flex h-full flex-col rounded-xl border border-gray-200 bg-white dark:border-slate-700 dark:bg-slate-800">
      <div
        class="flex flex-wrap items-center gap-2 border-b border-gray-200 px-2 py-2 sm:gap-3 sm:px-3 dark:border-slate-700"
        [style.--accent]="palette().accent">
        <!-- Slot optionnel en tête de barre (bascule Live / History de inside). -->
        <ng-content select="[logsToolbarStart]" />
        <div class="flex flex-wrap items-center gap-1">
          @for (lvl of levels; track lvl.value) {
            <button
              type="button"
              class="inline-flex h-7 items-center rounded-md border px-2.5 text-xs font-semibold transition-colors"
              [class.border-gray-300]="!isLevelActive(lvl.value)"
              [class.text-gray-600]="!isLevelActive(lvl.value)"
              [class.bg-white]="!isLevelActive(lvl.value)"
              [class.hover:bg-gray-50]="!isLevelActive(lvl.value)"
              [class.dark:border-slate-600]="!isLevelActive(lvl.value)"
              [class.dark:bg-slate-700]="!isLevelActive(lvl.value)"
              [class.dark:text-slate-200]="!isLevelActive(lvl.value)"
              [class.dark:hover:bg-slate-600]="!isLevelActive(lvl.value)"
              [style.background]="isLevelActive(lvl.value) ? palette().accent : ''"
              [style.border-color]="isLevelActive(lvl.value) ? palette().accent : ''"
              [style.color]="isLevelActive(lvl.value) ? '#fff' : ''"
              (click)="toggleLevel(lvl.value)">
              {{ lvl.name }}
            </button>
          }
        </div>

        <span class="ml-auto text-xs font-semibold text-gray-500 dark:text-slate-400">
          {{ filteredLogs().length }} / {{ logs().length }} lines
        </span>

        <div class="flex items-center gap-1.5">
          <wh-button
            variant="outline"
            size="xs"
            [disabled]="logs().length === 0"
            title="Download the visible buffer as a .txt"
            (click)="onDownload()">
            <svg class="size-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2">
              <path stroke-linecap="round" stroke-linejoin="round" d="M12 4v12m0 0l-4-4m4 4l4-4M4 20h16" />
            </svg>
            Download
          </wh-button>
          <wh-button
            variant="destructiveOutline"
            size="xs"
            [disabled]="logs().length === 0"
            title="Clear the buffer"
            (click)="onClear()">
            <svg class="size-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2">
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6M1 7h22M9 7V3a1 1 0 011-1h4a1 1 0 011 1v4" />
            </svg>
            Clear
          </wh-button>
        </div>
      </div>

      <!-- Terminal — auto-scrolls to bottom unless the operator scrolled up. -->
      <div #terminal class="terminal" (scroll)="onTerminalScroll($event)">
        @if (formattedLogs().length === 0) {
          <div class="flex h-full items-center justify-center text-gray-500 dark:text-slate-400">
            @if (logs().length === 0) {
              No logs yet — waiting for the device to publish.
            } @else {
              No log line matches the current level filter.
            }
          </div>
        } @else {
          @for (html of formattedLogs(); track $index) {
            <div class="terminal-line" [innerHTML]="html"></div>
          }
        }
      </div>
    </div>
  `,
})
export class LogsViewerComponent implements AfterViewChecked {
  // ── Inputs
  /** The live log buffer — caller passes the store signal value. */
  logs = input.required<string[]>();

  /** Device id used in the download filename. */
  deviceId = input.required<string>();

  /** Visual theme — accent colour for the active level chip + button focus. */
  theme = input<Theme>('tracker');

  /** Optional one-off seed (e.g. {@code Tracker.lastLog} or
   * {@code Gateway.lastLogSaved}). When this lands non-empty AND {@link logs}
   * is empty, we emit {@link seedLogs} so the host can push it into the store
   * exactly once. Subsequent updates of {@link initialLog} are ignored — the
   * live WS sub takes over after the first frame. */
  initialLog = input<string | undefined | null>(null);

  // ── Outputs
  /** Fires when Clear is clicked — host clears the underlying buffer. */
  clearLogsOutput = output<void>();

  /** Fires once when {@link initialLog} arrives and {@link logs} is empty —
   * payload is the parsed lines ready to push into the store. */
  seedLogs = output<string[]>();

  // ── State
  protected readonly levels = LEVELS;

  /** Currently active level filters. Empty = show everything. */
  protected readonly selectedLevels = signal<number[]>([]);

  protected readonly filteredLogs = computed<string[]>(() => {
    const all = this.logs();
    const active = this.selectedLevels();
    if (active.length === 0) return all;
    const matches = active.map(v => LEVELS.find(l => l.value === v)?.match).filter((m): m is string => !!m);
    return all.filter(line => matches.some(m => line.includes(m)));
  });

  /** Pre-formatted lines: ANSI escape codes converted to coloured spans + the
   * timestamp/bracket spans the inside terminal uses. Renders via
   * {@code [innerHTML]}. */
  protected readonly formattedLogs = computed<SafeHtml[]>(() =>
    this.filteredLogs().map(line => this.#sanitizer.bypassSecurityTrustHtml(this.#logService.formatLogLineRaw(line)))
  );

  /** Resolved palette for the current theme. */
  protected readonly palette = computed(() => THEME[this.theme()]);

  // ── Auto-scroll
  private readonly terminal = viewChild<ElementRef<HTMLDivElement>>('terminal');
  private stickToBottom = true;
  private forceScrollNext = false;
  private lastLineCount = 0;

  // ── One-shot seed guard. We only emit seedLogs once per component instance
  // even if initialLog is updated repeatedly by the host.
  private seeded = false;

  #logService = inject(LogService);
  #sanitizer = inject(DomSanitizer);

  constructor() {
    // Seed initialLog into the host store the first time it arrives non-empty
    // while the live buffer is still cold. Once the WS sub starts pushing
    // frames, the buffer fills and the if-guard skips. Idempotent via #seeded
    // so a re-emission of the same blob doesn't double-feed.
    effect(
      () => {
        if (this.seeded) return;
        const blob = this.initialLog();
        if (!blob) return;
        if (this.logs().length > 0) {
          // Live buffer already populated by the WS sub — no need to seed.
          this.seeded = true;
          return;
        }
        const lines = blob.split(/\r?\n/).filter(l => l.length > 0);
        if (!lines.length) return;
        this.seedLogs.emit(lines);
        this.seeded = true;
        this.forceScrollNext = true;
      },
      { allowSignalWrites: true }
    );
  }

  ngAfterViewChecked(): void {
    const el = this.terminal()?.nativeElement;
    if (!el) return;
    const lineCount = this.filteredLogs().length;
    const grew = lineCount > this.lastLineCount;
    if ((this.stickToBottom && grew) || this.forceScrollNext) {
      el.scrollTop = el.scrollHeight;
      this.forceScrollNext = false;
    }
    this.lastLineCount = lineCount;
  }

  protected isLevelActive(value: number): boolean {
    return this.selectedLevels().includes(value);
  }

  protected toggleLevel(value: number): void {
    this.selectedLevels.update(curr => (curr.includes(value) ? curr.filter(v => v !== value) : [...curr, value]));
  }

  /** Detect whether the user is still pinned to the bottom — 32px slack so a
   * tiny rubber-band scroll near the edge doesn't break auto-stick. */
  protected onTerminalScroll(e: Event): void {
    const el = e.target as HTMLDivElement;
    this.stickToBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 32;
  }

  protected onClear(): void {
    this.clearLogsOutput.emit();
    this.forceScrollNext = true;
  }

  protected onDownload(): void {
    const blob = new Blob([this.logs().join('\n')], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    const stamp = new Date().toISOString().replace(/[:.]/g, '-');
    a.href = url;
    a.download = `${this.deviceId() || 'device'}-logs-${stamp}.txt`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }
}
